import * as Sentry from "@sentry/nextjs";

import {
  classifyReportability,
  type ReportabilityKind,
} from "@membentry/shared-observability";
import { logger } from "@membentry/shared-logger";
import { getApiError } from "@membentry/shared-api";

interface ReportWebErrorInput {
  error: unknown;
  context: string;
  kind: ReportabilityKind;
  escalate?: boolean;
  httpStatus?: number;
  errorCode?: string;
  tags?: Record<string, string | null | undefined>;
  extra?: Record<string, unknown>;
  fingerprint?: string[];
}

function toError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }

  return new Error(typeof error === "string" ? error : "Unknown web error");
}

export function reportWebError(input: ReportWebErrorInput): string | undefined {
  const error = toError(input.error);
  const decision = classifyReportability({
    kind: input.kind,
    escalate: input.escalate,
    httpStatus: input.httpStatus,
    errorCode: input.errorCode ?? getApiError(input.error)?.code,
  });

  logger.error(input.context, {
    error: error.message,
    kind: decision.kind,
    shouldReport: decision.shouldReport,
    reason: decision.reason,
    stack: error.stack,
    tags: input.tags,
    extra: input.extra,
  });

  if (!decision.shouldReport) {
    return undefined;
  }

  let eventId: string | undefined;

  Sentry.withScope((scope) => {
    scope.setTag("reportability.kind", decision.kind);
    scope.setTag("reportability.reason", decision.reason);

    for (const [key, value] of Object.entries(input.tags ?? {})) {
      if (typeof value === "string") {
        scope.setTag(key, value);
      }
    }

    for (const [key, value] of Object.entries(input.extra ?? {})) {
      scope.setExtra(key, value);
    }

    if (input.fingerprint?.length) {
      scope.setFingerprint(input.fingerprint);
    }

    eventId = Sentry.captureException(error);
  });

  return eventId;
}

export function throwReportedWebImpossibleState(input: {
  error?: unknown;
  message: string;
  context: string;
  tags?: Record<string, string | null | undefined>;
  extra?: Record<string, unknown>;
  fingerprint?: string[];
}): never {
  const error = toError(input.error ?? new Error(input.message));

  reportWebError({
    error,
    context: input.context,
    kind: "impossible-state",
    tags: input.tags,
    extra: input.extra,
    fingerprint: input.fingerprint,
  });

  throw error;
}

export function reportGlobalRenderError(error: Error & { digest?: string }) {
  return reportWebError({
    error,
    context: "Global error boundary caught a render failure",
    kind: "unexpected-runtime",
    tags: {
      seam: "app.global-error",
    },
    extra: {
      digest: error.digest,
    },
  });
}
